import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ChangeEvent,
  type PointerEvent,
} from "react";
import {
  dataActions,
  fetchEvents,
  fetchMembers,
  fetchRawOutlookEvents,
  settingsActions,
  useAppDispatch,
  useAppSelector,
} from "./store";
import {
  buildGridModel,
  colorAssignment,
  dateString,
  dayEvents,
  dayList,
  eventSetKey,
  eventVisible,
  hiddenEventSummary,
  HIDDEN_REASON_LABELS,
  HOURS,
  labelSlotSpan,
  UNASSIGNED_MEMBER_ID,
  WEEKDAYS_JA,
} from "./selectors";
import { sanitizeSettings } from "./settings";
import { AUTH_REQUIRED_MESSAGE, signIn } from "./auth";
import { getCapabilities } from "./api";
import EventEditorModal from "./EventEditorModal";
import OutlookImportPage from "./OutlookImport";
import type { Member, ScheduleEvent, Settings } from "./types";

function Controls({
  members,
  events,
  settings,
}: {
  members: Member[];
  events: ScheduleEvent[];
  settings: Settings;
}) {
  const dispatch = useAppDispatch();
  const availableMembers = members.filter((member) => !settings.memberIds.includes(member.id));
  const teamAddresses = useMemo(
    () =>
      [...new Set(events.flatMap((event) => event.teamAddresses ?? []))].sort((a, b) =>
        a.localeCompare(b),
      ),
    [events],
  );
  const [selectedMemberId, setSelectedMemberId] = useState("");

  useEffect(() => {
    if (!availableMembers.some((member) => member.id === selectedMemberId)) {
      setSelectedMemberId(availableMembers[0]?.id ?? "");
    }
  }, [availableMembers, selectedMemberId]);

  const exportSettings = () => {
    const blob = new Blob([JSON.stringify(settings, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "schedule-viewer-config.json";
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const importSettings = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      dispatch(settingsActions.loadSettings(sanitizeSettings(JSON.parse(await file.text()))));
    } catch {
      window.alert("設定ファイルを読み込めませんでした");
    }
    event.target.value = "";
  };

  return (
    <div className="controls">
      <div className="ctl">
        <label htmlFor="from">表示期間（開始）</label>
        <input
          type="date"
          id="from"
          value={settings.from}
          max={settings.to}
          onChange={(event) => dispatch(settingsActions.setPeriod({ from: event.target.value }))}
        />
      </div>
      <div className="ctl">
        <label htmlFor="to">表示期間（終了）</label>
        <input
          type="date"
          id="to"
          value={settings.to}
          min={settings.from}
          onChange={(event) => dispatch(settingsActions.setPeriod({ to: event.target.value }))}
        />
      </div>
      <div className="ctl">
        <label htmlFor="member-picker">メンバー追加</label>
        <div className="ctl-row">
          <select
            id="member-picker"
            value={selectedMemberId}
            disabled={availableMembers.length === 0}
            onChange={(event) => setSelectedMemberId(event.target.value)}
          >
            {availableMembers.map((member) => (
              <option key={member.id} value={member.id}>{member.name}</option>
            ))}
          </select>
          <button
            type="button"
            disabled={!selectedMemberId}
            onClick={() => selectedMemberId && dispatch(settingsActions.addMember(selectedMemberId))}
          >
            追加
          </button>
        </div>
      </div>
      <div className="ctl">
        <label htmlFor="min-h">最小表示時間 (h)</label>
        <input
          type="number"
          id="min-h"
          min="0"
          max="24"
          step="0.5"
          value={settings.minDurationHours}
          onChange={(event) => dispatch(settingsActions.setMinDuration(Number(event.target.value)))}
        />
      </div>
      {teamAddresses.length > 0 && (
        <div className="ctl ctl-wide">
          <span className="ctl-label">チーム予定フィルター</span>
          <div className="ctl-row options">
            {teamAddresses.map((address) => (
              <label className="chk" key={address}>
                <input
                  type="checkbox"
                  checked={!settings.hiddenTeamAddresses.includes(address)}
                  onChange={(event) =>
                    dispatch(
                      settingsActions.setTeamAddressVisible({
                        address,
                        visible: event.target.checked,
                      }),
                    )
                  }
                />
                {address.split("@")[0]} を表示
              </label>
            ))}
          </div>
        </div>
      )}
      <div className="ctl">
        <span className="ctl-label">表示条件</span>
        <div className="ctl-row options">
          <label className="chk">
            <input
              type="checkbox"
              checked={settings.showUnassignedEvents}
              onChange={(event) => dispatch(settingsActions.setBoolean({ key: "showUnassignedEvents", value: event.target.checked }))}
            />
            未割り当て予定を表示
          </label>
          <label className="chk">
            <input
              type="checkbox"
              checked={settings.showRawOutlookEvents}
              onChange={(event) => dispatch(settingsActions.setBoolean({ key: "showRawOutlookEvents", value: event.target.checked }))}
            />
            Outlook予定を表示（未登録の生データ）
          </label>
          <label className="chk">
            <input
              type="checkbox"
              checked={settings.rainbowEnabled}
              onChange={(event) => dispatch(settingsActions.setBoolean({ key: "rainbowEnabled", value: event.target.checked }))}
            />
            複数イベントを虹色
          </label>
          <label className="chk">
            <input
              type="checkbox"
              checked={settings.mergeEnabled}
              onChange={(event) => dispatch(settingsActions.setBoolean({ key: "mergeEnabled", value: event.target.checked }))}
            />
            縦結合（完全一致時）
          </label>
          <label className="chk">
            <input
              type="checkbox"
              checked={settings.showWeekends}
              onChange={(event) => dispatch(settingsActions.setBoolean({ key: "showWeekends", value: event.target.checked }))}
            />
            土日を表示
          </label>
        </div>
      </div>
      <div className="ctl">
        <span className="ctl-label">設定の保存・再現</span>
        <div className="ctl-row">
          <button type="button" onClick={exportSettings}>エクスポート</button>
          <label className="button-label">
            インポート
            <input type="file" accept=".json,application/json" onChange={(event) => void importSettings(event)} />
          </label>
        </div>
      </div>
    </div>
  );
}

function fillStyle(events: ScheduleEvent[], colors: Record<string, number>, settings: Settings): CSSProperties {
  if (events.length === 1) {
    const color = `var(--series-${colors[events[0]?.id ?? ""] ?? 1})`;
    return {
      background: `color-mix(in srgb, ${color} 32%, var(--surface-1))`,
      boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${color} 55%, transparent)`,
    };
  }
  if (settings.rainbowEnabled) {
    return {
      background: "linear-gradient(135deg, #e34948 0%, #eda100 25%, #1baf7a 50%, #2a78d6 75%, #4a3aa7 100%)",
      opacity: 0.55,
    };
  }
  const stops = events.map((event, index) => {
    const color = `var(--series-${colors[event.id] ?? 1})`;
    return `color-mix(in srgb, ${color} 40%, var(--surface-1)) ${(index / events.length) * 100}% ${((index + 1) / events.length) * 100}%`;
  });
  return { background: `linear-gradient(135deg, ${stops.join(",")})` };
}

function ScheduleGrid({
  members,
  events,
  settings,
  editMode,
  onEditEvents,
}: {
  members: Member[];
  events: ScheduleEvent[];
  settings: Settings;
  editMode: boolean;
  /** その日の予定一覧と、最初に選んでおく予定（クリックしたコマの予定）。 */
  onEditEvents: (events: ScheduleEvent[], focusEventId?: string) => void;
}) {
  const dispatch = useAppDispatch();
  const lastTouchRef = useRef<{ key: string; at: number } | null>(null);
  const [draggingMemberId, setDraggingMemberId] = useState<string | null>(null);
  const dragStartOrderRef = useRef<string[] | null>(null);
  const days = useMemo(() => dayList(settings), [settings]);
  const gridMembers = useMemo(
    () =>
      settings.showUnassignedEvents &&
      events.some((event) => event.memberIds.length === 0 && eventVisible(event, settings))
        ? [{ id: UNASSIGNED_MEMBER_ID, name: "未割り当て" }, ...members]
        : members,
    [events, members, settings],
  );
  const colors = useMemo(() => colorAssignment(events, settings), [events, settings]);
  const model = useMemo(
    () => buildGridModel(events, gridMembers, days, settings),
    [events, gridMembers, days, settings],
  );

  const sameKey = (memberIndex: number, dayIndex: number, hourIndex: number, key: string) =>
    memberIndex >= 0 &&
    memberIndex < gridMembers.length &&
    dayIndex >= 0 &&
    dayIndex < days.length &&
    hourIndex >= 0 &&
    hourIndex < HOURS.length &&
    model[memberIndex]?.[dayIndex]?.[hourIndex]?.key === key;

  const handleTouch = (
    event: PointerEvent<HTMLElement>,
    slotEvents: ScheduleEvent[],
    openEditor: () => void,
  ) => {
    if (!editMode || event.pointerType !== "touch") return;
    if (!slotEvents.some((slotEvent) => !slotEvent.live)) return;
    const now = Date.now();
    const key = eventSetKey(slotEvents);
    if (lastTouchRef.current?.key === key && now - lastTouchRef.current.at <= 400) {
      lastTouchRef.current = null;
      openEditor();
      return;
    }
    lastTouchRef.current = { key, at: now };
  };

  /** 未割り当て行は常に先頭固定なので並び替えの対象外。 */
  const moveMemberTo = (memberId: string, toIndex: number) => {
    if (memberId === UNASSIGNED_MEMBER_ID) return;
    dispatch(settingsActions.moveMember({ memberId, toIndex }));
  };

  /**
   * ↑↓キーは表示中の隣の行と入れ替える。settings.memberIds には Notion から
   * 取得できなかったメンバー（Inactive化された利用者を含む共有URLなど）のIDが
   * 残ることがあるため、行番号ではなく表示行の隣を基準にする。
   */
  const moveMemberBy = (memberId: string, offset: number) => {
    const visibleIds = members.map((member) => member.id);
    const position = visibleIds.indexOf(memberId);
    if (position < 0) return;
    const neighborId = visibleIds[position + offset];
    if (!neighborId) return;
    const toIndex = settings.memberIds.indexOf(neighborId);
    if (toIndex < 0) return;
    moveMemberTo(memberId, toIndex);
  };

  /** ドラッグをEscで中止した／ドロップ先が無かったときは開始前の並びへ戻す。 */
  const endDrag = (dropEffect: string) => {
    const startOrder = dragStartOrderRef.current;
    dragStartOrderRef.current = null;
    setDraggingMemberId(null);
    if (startOrder && dropEffect === "none") {
      dispatch(settingsActions.setMemberOrder(startOrder));
    }
  };

  /** ドラッグ中はホバーした行の位置へ即時移動し、そのまま並びをプレビューさせる。 */
  const dragOverMember = (targetMemberId: string) => {
    if (!draggingMemberId || targetMemberId === draggingMemberId) return;
    const toIndex = settings.memberIds.indexOf(targetMemberId);
    if (toIndex < 0) return;
    moveMemberTo(draggingMemberId, toIndex);
  };

  return (
    <div className={`grid-scroll${editMode ? " edit-mode" : ""}`}>
      {gridMembers.length === 0 ? (
        <p className="empty">表示するメンバーがいません。上のメニューから追加してください。</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th className="member">担当者</th>
              {days.map((day) => (
                <th className="day" colSpan={HOURS.length} key={dateString(day)}>
                  {day.getMonth() + 1}/{day.getDate()}({WEEKDAYS_JA[day.getDay()]})
                </th>
              ))}
            </tr>
            <tr>
              <th className="member" />
              {days.flatMap((day) => HOURS.map((hour) => (
                <th className="hour" key={`${dateString(day)}-${hour}`}>{hour}</th>
              )))}
            </tr>
          </thead>
          <tbody>
            {gridMembers.map((member, memberIndex) => {
              const reorderable = member.id !== UNASSIGNED_MEMBER_ID && members.length > 1;
              const rowClass = [
                member.id === UNASSIGNED_MEMBER_ID ? "unassigned-row" : "",
                draggingMemberId === member.id ? "dragging-row" : "",
              ]
                .filter(Boolean)
                .join(" ");
              return (
              <tr
                className={rowClass || undefined}
                key={member.id}
                onDragOver={(event) => {
                  if (!draggingMemberId || !reorderable) return;
                  event.preventDefault();
                  event.dataTransfer.dropEffect = "move";
                  dragOverMember(member.id);
                }}
                onDrop={(event) => {
                  if (!draggingMemberId) return;
                  event.preventDefault();
                  dragStartOrderRef.current = null;
                  setDraggingMemberId(null);
                }}
              >
                <td
                  className="member"
                  draggable={reorderable}
                  onDragStart={(event) => {
                    if (!reorderable) {
                      event.preventDefault();
                      return;
                    }
                    dragStartOrderRef.current = settings.memberIds;
                    setDraggingMemberId(member.id);
                    event.dataTransfer.effectAllowed = "move";
                    event.dataTransfer.setData("text/plain", member.id);
                  }}
                  onDragEnd={(event) => endDrag(event.dataTransfer.dropEffect)}
                >
                  {reorderable && (
                    <span
                      className="drag-handle"
                      tabIndex={0}
                      title="ドラッグ、または上下キーで行を入れ替え"
                      aria-label={`${member.name}の行を並び替え`}
                      aria-keyshortcuts="ArrowUp ArrowDown"
                      onKeyDown={(event) => {
                        if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
                        event.preventDefault();
                        moveMemberBy(member.id, event.key === "ArrowUp" ? -1 : 1);
                      }}
                    >
                      ⠿
                    </span>
                  )}
                  {member.name}{" "}
                  {member.id !== UNASSIGNED_MEMBER_ID && (
                    <button
                      className="rm"
                      type="button"
                      title={`${member.name}の行を削除`}
                      aria-label={`${member.name}の行を削除`}
                      onClick={() => dispatch(settingsActions.removeMember(member.id))}
                    >
                      ✕
                    </button>
                  )}
                </td>
                {days.flatMap((day, dayIndex) => {
                  const dayCells = model[memberIndex]?.[dayIndex] ?? [];
                  return HOURS.map((hour, hourIndex) => {
                    const cell = dayCells[hourIndex];
                    const key = cell?.key ?? "";
                    const slotEvents = cell?.events ?? [];
                    const liveEvents = cell?.liveEvents ?? [];
                    const canEditSlot = editMode && slotEvents.length > 0;
                    // 編集モーダルには、クリックしたコマの予定だけでなく同じ日に表示中の予定を全部渡す。
                    // 日単位の集計は操作されたときだけ行う（描画ごとに全セル分は計算しない）。
                    const openSlotEditor = () => onEditEvents(dayEvents(dayCells), slotEvents[0]?.id);
                    const sameLeft = hourIndex > 0 && sameKey(memberIndex, dayIndex, hourIndex - 1, key);
                    const sameRight = hourIndex < HOURS.length - 1 && sameKey(memberIndex, dayIndex, hourIndex + 1, key);
                    const sameUp = settings.mergeEnabled && sameKey(memberIndex - 1, dayIndex, hourIndex, key);
                    const sameDown = settings.mergeEnabled && sameKey(memberIndex + 1, dayIndex, hourIndex, key);
                    const classes = ["fill"];
                    if (sameLeft) classes.push("jl");
                    if (sameRight) classes.push("jr");
                    if (sameUp) classes.push("ju");
                    if (sameDown) classes.push("jd");
                    if (!sameLeft && !sameUp) classes.push("rtl");
                    if (!sameRight && !sameUp) classes.push("rtr");
                    if (!sameLeft && !sameDown) classes.push("rbl");
                    if (!sameRight && !sameDown) classes.push("rbr");
                    const titles = slotEvents.map((event) => event.title).join(" ＋ ");
                    const liveTitles = liveEvents.map((event) => event.title).join("、");
                    const tooltip = [
                      slotEvents.length || liveEvents.length
                        ? `${member.name} / ${day.getMonth() + 1}/${day.getDate()} ${hour}:00-${hour + 1}:00`
                        : "",
                      titles,
                      liveTitles ? `Outlook未登録: ${liveTitles}` : "",
                      canEditSlot ? "ダブルクリックで編集" : "",
                    ]
                      .filter(Boolean)
                      .join("｜");
                    const showLabel = Boolean(key) && !sameLeft && !sameUp;
                    // ラベルは次の予定が始まるコマまでを上限にし、収まらない分は「…」で省く。
                    // 1コマ幅は「…」を付けると1文字しか残らないため、省略記号なしで先頭を見せる。
                    const labelSpan = showLabel ? labelSlotSpan(dayCells, hourIndex) : 0;
                    const tightLabel = labelSpan === 1;
                    return (
                      <td
                        className={`slot${hourIndex === 0 ? " day-start" : ""}${canEditSlot ? " editable" : ""}`}
                        key={`${dateString(day)}-${hour}`}
                        title={tooltip || undefined}
                        onDoubleClick={() => canEditSlot && openSlotEditor()}
                        onPointerUp={(event) => handleTouch(event, slotEvents, openSlotEditor)}
                        role={canEditSlot ? "button" : undefined}
                        tabIndex={canEditSlot ? 0 : undefined}
                        onKeyDown={(event) => {
                          if (canEditSlot && (event.key === "Enter" || event.key === " ")) {
                            event.preventDefault();
                            openSlotEditor();
                          }
                        }}
                      >
                        {key && <span className={classes.join(" ")} style={fillStyle(slotEvents, colors, settings)} />}
                        {showLabel && (
                          <span
                            className={tightLabel ? "lbl tight" : "lbl"}
                            style={{ "--label-span": labelSpan } as CSSProperties}
                          >
                            {slotEvents.length > 1 && !tightLabel ? `＊${titles}` : titles}
                          </span>
                        )}
                        {liveEvents.length > 0 && <span className="live-bar" aria-hidden="true" />}
                      </td>
                    );
                  });
                })}
              </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Legend({
  events,
  settings,
  editMode,
  onEditEvents,
}: {
  events: ScheduleEvent[];
  settings: Settings;
  editMode: boolean;
  onEditEvents: (events: ScheduleEvent[]) => void;
}) {
  const colors = colorAssignment(events, settings);
  // 生 Outlook は件数が多く凡例が埋まるため、代表チップ1つにまとめる。
  const visibleEvents = events.filter((event) => !event.live && eventVisible(event, settings));
  const liveCount = events.filter((event) => event.live && eventVisible(event, settings)).length;
  const lastTouchRef = useRef<{ eventId: string; at: number } | null>(null);
  const handleTouch = (pointerEvent: PointerEvent<HTMLButtonElement>, event: ScheduleEvent) => {
    if (!editMode || pointerEvent.pointerType !== "touch") return;
    const now = Date.now();
    if (lastTouchRef.current?.eventId === event.id && now - lastTouchRef.current.at <= 400) {
      lastTouchRef.current = null;
      onEditEvents([event]);
      return;
    }
    lastTouchRef.current = { eventId: event.id, at: now };
  };
  return (
    <div className="legend" aria-label="イベント凡例">
      {visibleEvents.map((event) => (
        <button
          type="button"
          className={`item legend-event${editMode ? " editable" : ""}`}
          key={event.id}
          title={editMode ? "ダブルクリックで担当者を編集" : undefined}
          onDoubleClick={() => editMode && onEditEvents([event])}
          onPointerUp={(pointerEvent) => handleTouch(pointerEvent, event)}
          onKeyDown={(keyEvent) => {
            if (editMode && (keyEvent.key === "Enter" || keyEvent.key === " ")) {
              keyEvent.preventDefault();
              onEditEvents([event]);
            }
          }}
        >
          <span
            className="chip"
            style={{ background: `color-mix(in srgb, var(--series-${colors[event.id] ?? 1}) 60%, var(--surface-1))` }}
          />
          {event.title}{event.allDay ? "（終日）" : ""}
        </button>
      ))}
      <span className="item"><span className="chip rainbow" />複数イベント重複</span>
      {settings.showRawOutlookEvents && (
        <span className="item">
          <span className="chip outlook-live" />Outlook未登録 {liveCount}件（帯のみ・表示専用）
        </span>
      )}
    </div>
  );
}

export default function App() {
  const dispatch = useAppDispatch();
  const settings = useAppSelector((state) => state.settings);
  const data = useAppSelector((state) => state.data);
  const [signingIn, setSigningIn] = useState(false);
  const [signInError, setSignInError] = useState<string | null>(null);
  const [canEditEvents, setCanEditEvents] = useState(false);
  const [editMode, setEditMode] = useState(false);
  // モーダルに出す予定は ID で覚え、表示内容は最新の store から引く。保存・削除後も
  // モーダルを開いたままにして、同じ日の別の予定を続けて編集できるようにするため。
  const [editingEventIds, setEditingEventIds] = useState<string[]>([]);
  const [editingFocusId, setEditingFocusId] = useState<string | undefined>(undefined);
  // モーダルを開き直したときだけ内部状態をリセットするためのキー。削除で一覧が減っても
  // 再マウントさせない（再マウントすると通知・選択・チェック状態が消える）。
  const [editorSession, setEditorSession] = useState(0);
  const [editNotice, setEditNotice] = useState<string | null>(null);
  const importMode = new URLSearchParams(window.location.search).get("mode") === "import";

  useEffect(() => {
    const request = dispatch(fetchMembers());
    return () => request.abort();
  }, [dispatch]);

  useEffect(() => {
    const controller = new AbortController();
    void getCapabilities(controller.signal)
      .then((capabilities) => setCanEditEvents(capabilities.canEditEvents))
      .catch(() => setCanEditEvents(false));
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (importMode) return;
    const request = dispatch(fetchEvents({ from: settings.from, to: settings.to }));
    return () => request.abort();
  }, [dispatch, importMode, settings.from, settings.to]);

  const memberIdsKey = settings.memberIds.join(",");
  useEffect(() => {
    if (importMode) return;
    if (!settings.showRawOutlookEvents) {
      dispatch(dataActions.clearRawOutlookEvents());
      return;
    }
    const request = dispatch(
      fetchRawOutlookEvents({
        from: settings.from,
        to: settings.to,
        memberIds: memberIdsKey ? memberIdsKey.split(",") : [],
      }),
    );
    return () => request.abort();
  }, [dispatch, importMode, memberIdsKey, settings.from, settings.to, settings.showRawOutlookEvents]);

  const selectedMembers = settings.memberIds
    .map((id) => data.members.find((member) => member.id === id))
    .filter((member): member is Member => Boolean(member));
  const timelineEvents = useMemo(
    () => (settings.showRawOutlookEvents ? [...data.events, ...data.rawOutlookEvents] : data.events),
    [data.events, data.rawOutlookEvents, settings.showRawOutlookEvents],
  );
  const loading =
    data.membersStatus === "loading" ||
    data.eventsStatus === "loading" ||
    data.rawOutlookStatus === "loading";
  const error = signInError || data.membersError || data.eventsError;
  const visibleEventCount = timelineEvents.filter((event) => eventVisible(event, settings)).length;
  const rawOutlookCount = data.rawOutlookEvents.filter((event) =>
    eventVisible(event, settings),
  ).length;
  const hiddenEvents = hiddenEventSummary(timelineEvents, settings);
  const editingEvents = useMemo(
    () =>
      editingEventIds
        .map((id) => data.events.find((event) => event.id === id))
        .filter((event): event is ScheduleEvent => Boolean(event)),
    [data.events, editingEventIds],
  );

  /** 生 Outlook 予定は Notion のページではないので編集対象から外す。 */
  const openEditor = (events: ScheduleEvent[], focusEventId?: string) => {
    const editable = events.filter((event) => !event.live);
    setEditNotice(editable.length === events.length ? null : "Outlookの生予定は編集できません");
    if (editable.length === 0) return;
    setEditingEventIds(editable.map((event) => event.id));
    setEditingFocusId(
      editable.some((event) => event.id === focusEventId) ? focusEventId : editable[0]?.id,
    );
    setEditorSession((current) => current + 1);
  };
  const closeEditor = useCallback(() => {
    setEditingEventIds([]);
    setEditingFocusId(undefined);
  }, []);

  const handleSignIn = async () => {
    setSigningIn(true);
    setSignInError(null);
    try {
      await signIn();
      window.location.reload();
    } catch {
      setSignInError("サインインを完了できませんでした");
      setSigningIn(false);
    }
  };

  if (importMode) {
    return (
      <OutlookImportPage
        members={data.members}
        initialMemberId={settings.memberIds[0]}
        initialFrom={settings.from}
        initialTo={settings.to}
        membersError={signInError || data.membersError}
        membersLoading={data.membersStatus === "loading"}
        signingIn={signingIn}
        onSignIn={() => void handleSignIn()}
      />
    );
  }

  return (
    <main className="wrap">
      <div className="page-heading">
        <div>
          <h1>チームスケジュールビュワー<span className="badge live">Notion</span></h1>
          <p className="sub">行 = メンバー（Notion メンバーマスター）／ 列 = 日付 × 時間（10:00–17:00）</p>
        </div>
        <div className="page-actions">
          <div className="mode-switch" role="group" aria-label="操作モード">
            <button
              type="button"
              aria-pressed={!editMode}
              className={!editMode ? "active" : ""}
              onClick={() => {
                setEditMode(false);
                closeEditor();
              }}
            >
              閲覧モード
            </button>
            <button
              type="button"
              aria-pressed={editMode}
              className={editMode ? "active" : ""}
              disabled={!canEditEvents}
              title={canEditEvents ? "イベント担当者を編集します" : "編集権限がありません"}
              onClick={() => setEditMode(true)}
            >
              編集モード
            </button>
          </div>
          {canEditEvents && (
            <a className="nav-link" href={`/?mode=import${window.location.hash}`}>Outlookから予定を登録</a>
          )}
        </div>
      </div>

      <Controls members={data.members} events={timelineEvents} settings={settings} />

      {editMode && (
        <div className="edit-hint" role="status">
          編集モード：予定のセルまたは凡例をダブルクリック／ダブルタップすると担当者を編集できます。
        </div>
      )}
      {editNotice && <div className="status success" role="status">{editNotice}</div>}

      <div className={`status ${error ? "error" : ""}`} role="status" aria-live="polite">
        {error ? (
          <>
            <span>{error}</span>
            {error === AUTH_REQUIRED_MESSAGE ? (
              <button type="button" disabled={signingIn} onClick={() => void handleSignIn()}>
                {signingIn ? "サインイン中…" : "Microsoft 365でサインイン"}
              </button>
            ) : (
              <button type="button" onClick={() => window.location.reload()}>再読み込み</button>
            )}
          </>
        ) : loading ? (
          <span className="loading"><span className="spinner" />予定を読み込み中…</span>
        ) : (
          <span>
            Active {data.members.length}名・イベント {visibleEventCount}件を表示中
            {settings.showRawOutlookEvents && (
              <span className="outlook-note">
                （うちOutlook未登録 {rawOutlookCount}件
                {data.rawOutlookError ? "・取得に失敗しました" : ""}）
              </span>
            )}
            {hiddenEvents.length > 0 && (
              <span className="hidden-note">
                （表示条件で非表示:{" "}
                {hiddenEvents
                  .map(({ reason, count }) => `${HIDDEN_REASON_LABELS[reason]} ${count}件`)
                  .join("・")}
                ）
              </span>
            )}
          </span>
        )}
      </div>

      <ScheduleGrid
        members={selectedMembers}
        events={timelineEvents}
        settings={settings}
        editMode={editMode}
        onEditEvents={openEditor}
      />
      <Legend
        events={timelineEvents}
        settings={settings}
        editMode={editMode}
        onEditEvents={openEditor}
      />

      {editingEvents.length > 0 && (
        <EventEditorModal
          key={editorSession}
          events={editingEvents}
          initialEventId={editingFocusId}
          members={data.members}
          onClose={closeEditor}
          onSaved={(eventId, memberIds) => {
            dispatch(dataActions.updateEventParticipants({ eventId, memberIds }));
            setEditNotice(
              memberIds.length === 0
                ? "イベントから全員の割り当てを解除しました"
                : `イベントの担当者を${memberIds.length}名に更新しました`,
            );
          }}
          onDeleted={(eventId) => {
            dispatch(dataActions.removeEvent(eventId));
            // 削除した予定は一覧からも外す。残りが無ければモーダルは閉じる。
            setEditingEventIds((current) => current.filter((id) => id !== eventId));
            setEditNotice("イベントをNotionのゴミ箱へ移動しました");
          }}
        />
      )}

      <p className="footer">
        セル結合ルール: 上下に隣接するメンバーの同一時間コマが<strong>完全に同じイベント集合</strong>を持つ場合のみ縦に結合表示。
        複数イベントが重なるコマは虹色グラデーション（結合は複数イベントの集合が完全一致のときのみ）。<br />
        設定（表示メンバー・期間・フィルタ条件）はURLハッシュに自動保存されます。このURLを共有すれば同じビューが再現されます。
        タイムラインの色付きセルはNotionへ登録済みの予定です。「Outlook予定を表示」をオンにすると、Notion未登録の予定がセル下端の斜線帯で重なります（表示専用・編集不可、件名はマウスオーバーで確認）。
      </p>
    </main>
  );
}
